import { formatMoney } from 'accounting'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faBan, faCog, faPencil } from '@fortawesome/pro-light-svg-icons'

import {
  Box, Button, IconWithMenu, Image, Checkbox, Link, Text,
} from '@domain-group/suit-theme'

import useOrder from '@hooks/useOrder'

import type { OrderModel } from '@domain-group/realhub-backend-api-types'

import EnabledPermission from '@components/EnabledPermission'

import { getCampaignType } from '@functions/campaign'

import OrderContainer from '../../../../components/OrderContainer'

import OrderItem from './OrderItem'

interface ApprovedOrderProps {
  callbacks: {
    selectOrderId: VoidFunction,
  },
  isSelected: boolean,
  order: OrderModel,
}

const ApprovedOrder = (props: ApprovedOrderProps) => {
  const {
    callbacks: { selectOrderId },
    isSelected = false,
    order,
  } = props

  const orderPayload = useOrder(order)
  const {
    campaign,
    callbacks: { editOrderTotal, unapproveOrderConfirmation },
    filteredOrderItems,
    isPublicationOrder,
    isUnapprovable,
    order: { id, total },
    processingFees,
    provider,
    publication,
    totalEditable,
  } = orderPayload

  const logoUrl = isPublicationOrder ? publication?.logo?.thumb : provider?.alternate_logo?.thumb

  const showAdditionalOptions = isUnapprovable

  return (
    <OrderContainer>
      <OrderContainer.Items>
        {filteredOrderItems.map((orderItem, index) => (
          <OrderItem key={orderItem.id} last={index === filteredOrderItems.length - 1} orderItem={orderItem} />
        ))}
      </OrderContainer.Items>

      <OrderContainer.Footer>
        <Checkbox checked={isSelected} onClick={selectOrderId} />

        <Box alignItems="center" width={['100%', 'auto']}>
          <Image
            backgroundSize="contain"
            borderRadius={5}
            boxProps={{ border: '1px solid', borderColor: 'lineColor' }}
            height={38}
            url={logoUrl!}
            width={60}
          />
          <Box flexDirection="column" fontSize="xsmall" marginLeft="large">
            <Text color="lightGrey">Order #{id}</Text>

            <Text color="bodyFontLightColor" marginTop="small">
              from {provider?.title}
            </Text>
          </Box>
        </Box>

        <Box
          alignItems="center"
          justifyContent="flex-end"
          marginLeft={[0, 'auto']}
          marginTop={['large', 0]}
          width={['100%', 'auto']}
        >
          <Box
            alignItems={['flex-start', 'flex-end']}
            flexDirection="column"
            flexShrink={0}
            justifyContent="center"
            marginRight={['auto', 0]}
            width="auto"
          >
            <Box alignItems="center" width="auto">
              <Text fontSize="medium">{formatMoney(total)}</Text>
              {totalEditable && (
                <Link
                  onClick={editOrderTotal}
                  textProps={{ color: 'lightGrey', fontSize: 'xsmall', marginLeft: 'small' }}
                >
                  <FontAwesomeIcon icon={faPencil} />
                </Link>
              )}
            </Box>

            {processingFees > 0 && (
              <Text color="bodyFontLightColor" fontSize="xsmall" marginTop="small">
                + {formatMoney(processingFees)} Fees
              </Text>
            )}
          </Box>

          {showAdditionalOptions && (
            <Box alignItems="center" marginLeft="large" width="auto">
              <IconWithMenu
                boxProps={{ position: 'relative' }}
                icon={(
                  <Button
                    buttonStyle="secondaryUtility"
                    icon={<FontAwesomeIcon icon={faCog} />}
                    size="medium"
                    style={{ height: 38 }}
                  />
                )}
              >
                {/*
                 // @ts-expect-error TS2741 */}
                <IconWithMenu.Menu closeOnClick>
                  <EnabledPermission
                    campaignType={getCampaignType(campaign)}
                    permissionKey="campaign.artwork.unapprove"
                  >
                    <Link
                      key="delete"
                      onClick={() => unapproveOrderConfirmation()}
                    >
                      <FontAwesomeIcon icon={faBan} /> Unapprove Order
                    </Link>
                  </EnabledPermission>
                </IconWithMenu.Menu>
              </IconWithMenu>
            </Box>
          )}
        </Box>
      </OrderContainer.Footer>
    </OrderContainer>
  )
}

export default ApprovedOrder
